import type { RoleType } from './auth.types'

/**
 * Maps role values to their display names
 */
export const ROLE_DISPLAY_MAP: Record<RoleType, string> = {
  guest: 'Guest',
  customer: 'Customer',
}

/**
 * User roles in the application
 */
export type UserRole = RoleType

/**
 * Role hierarchy for permission checking
 * Higher roles inherit permissions from lower roles
 */
export const ROLE_HIERARCHY: Record<RoleType, number> = {
  guest: 1,
  customer: 2,
}

/**
 * Gets the display name for a role
 * @param role - The role value
 * @returns The formatted display name, or the capitalized role if not found
 */
export function getRoleDisplayName(role: RoleType | string | undefined | null): string {
  if (!role) return 'User'

  // Check if it's a valid RoleType
  if (role in ROLE_DISPLAY_MAP) {
    return ROLE_DISPLAY_MAP[role as RoleType]
  }

  // Fallback: capitalize first letter
  return role.charAt(0).toUpperCase() + role.slice(1)
}

/**
 * Gets all role display names
 * @returns Array of role display names
 */
export function getAllRoleDisplayNames(): string[] {
  return Object.values(ROLE_DISPLAY_MAP)
}

/**
 * Gets role value from display name (reverse lookup)
 * @param displayName - The display name
 * @returns The role value, or null if not found
 */
export function getRoleFromDisplayName(displayName: string): RoleType | null {
  const entry = Object.entries(ROLE_DISPLAY_MAP).find(([, value]) => value === displayName)
  return entry ? (entry[0] as RoleType) : null
}

/**
 * Check if a user role has access based on required roles
 * @param userRole - The user's current role
 * @param allowedRoles - Array of roles that can access the resource
 * @returns true if user has access, false otherwise
 */
export function hasRoleAccess(userRole: string | undefined, allowedRoles: UserRole[]): boolean {
  if (!userRole) return false

  // Check if user's role is in the allowed roles list
  return allowedRoles.includes(userRole as UserRole)
}

/**
 * Check if user has any of the required roles
 * @param userRole - The user's current role
 * @param requiredRoles - Array of roles required
 * @returns true if user has one of the required roles
 */
export function hasAnyRole(userRole: string | undefined, requiredRoles: UserRole[]): boolean {
  return hasRoleAccess(userRole, requiredRoles)
}

/**
 * Check if user has all of the required roles (for future use if needed)
 * Note: A user typically has one role, so this is mainly for future extensibility
 */
export function hasAllRoles(userRole: string | undefined, requiredRoles: UserRole[]): boolean {
  if (!userRole) return false
  return requiredRoles.every((role) => role === userRole)
}

/**
 * Get user initials from full name
 * @param name - The full name
 * @returns Initials (max 2 characters)
 */
export const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
